<script setup lang="ts">
import './assets/style.scss'
import { useConfirm, VIcons, VModal } from '@/shared/ui'
import { useModel } from './../model/model.ts'
import { computed, type PropType, watch } from 'vue'
import { trim } from 'lodash'
import { useSearchPresetSave } from '@/features/GlobalSearch/PresetSave/model/api.ts'
import type { FilterCondition, SearchPresetItem } from '@/widgets/GlobalSearch/model/type.ts'
import { useTranslate } from '@/shared/lib'

const props = defineProps({
  presets: {
    type: Array as PropType<SearchPresetItem[]>,
    required: true,
  },
  presetId: {
    type: Number as PropType<number | null>,
    default: null,
  },
})

const filters = defineModel<FilterCondition[][]>({ default: [] })

const { open, data, clearData, setData } = useModel()
const { save } = useSearchPresetSave()
const { show } = useConfirm()
const { trs } = useTranslate()

const hasConditions = computed(() => filters.value.some((group) => group.length > 0))
const currentPreset = computed<SearchPresetItem | null>(
  () => props.presets.find((preset) => preset.id === props.presetId) ?? null,
)

watch(open, () => {
  open.value ? setData(filters.value) : clearData()
})

/**
 * Создаёт новый пресет из формы модалки.
 */
const submitHandler = async () => {
  try {
    await save({
      name: data.value.name,
      items: data.value.items,
    })
    open.value = false
  } catch {
    /* ошибка уже в toaste; модалку оставляем открытой */
  }
}

/**
 * Открываем подтверждение создание нового или сохранения старого пресета
 */
const openHandler = () => {
  const preset = currentPreset.value

  if (!preset?.id) {
    open.value = true
    return
  }

  show({
    title: trs('itf.save_or_create_preset'),
    confirmFunc: () =>
      save({
        id: preset.id,
        name: preset.name,
        items: filters.value,
      }),
    cancelFunc: () => {
      open.value = true
    },
    confirmBtn: trs('itf.save'),
    cancelBtn: trs('itf.create_new'),
  })
}
</script>

<template>
  <VBtn color="default" variant="tonal" :disabled="!hasConditions" @click="openHandler">
    <VIcons type="save" />
  </VBtn>

  <VModal :title="$t('itf.save_preset')" v-model="open">
    <p>{{ $t('itf.save_preset_info') }}</p>

    <VForm class="mt-4" @submit.prevent="submitHandler">
      <VTextField :label="$t('itf.name')" v-model="data.name" />

      <div class="form__btn_right">
        <VBtn color="default" variant="tonal" @click="open = false">
          {{ $t('itf.cancel') }}
        </VBtn>

        <VBtn type="submit" :disabled="!trim(data.name)">
          {{ $t('itf.save') }}
        </VBtn>
      </div>
    </VForm>
  </VModal>
</template>
